/**
 * The session page's words (C1, C3): every box on the session page, its
 * states, details, effects and blockers, built from typed data. The server
 * writes reasons; these are the client's own sentences. No em or en dash.
 */
import type { MeetingProvider } from "@/lib/api/tenant";

const JOIN_VERB: Record<MeetingProvider, string> = {
  zoom: "Join in Zoom",
  google_meet: "Join in Meet",
  in_person: "Join the meeting",
  other: "Join the meeting",
};

export const SESSION_COPY = {
  teach: {
    label: "Teach",
    job: "What happens during the session, and the way into its meeting.",
    now: "Now",
    ended: "Ended",
    cancelled: "Cancelled",
    starts: (when: string) => `Starts ${when}`,
    /** The provider's own page, in a new tab; the verb names the provider (CASE-027). */
    join: (provider: MeetingProvider | null) => (provider ? JOIN_VERB[provider] : "Join the meeting"),
    change: "Change where it meets",
    whereLabel: "Where it meets",
  },
  prepare: {
    label: "Prepare",
    job: "Before class: the learner-facing plan and its readings, published to learners when ready.",
    plan: "Session plan",
    readings: "Readings",
    published: "Published",
    changed: "Changed, not published",
    /** The held-session face names what is actually unpublished. */
    // A plan learners already see has changes, not a first publication.
    unpublished: (plan: false | "new" | "changed", readings: number) => [plan === "new" ? "Plan" : plan === "changed" ? "Plan changes" : null, readings ? `${readings} ${readings === 1 ? "reading" : "readings"}` : null].filter(Boolean).join(" and ") + " not published",
    readingsUnread: "Readings couldn't be read",
    readingReadings: "Reading readings",
    none: "None",
    notPublished: "Not published",
    noPlan: "No session plan",
    planUnread: "The session plan couldn't be read.",
    planLoading: "Reading the session plan",
    notConfirmed: "Publication not confirmed",
    saveNotConfirmed: "Plan save not confirmed",
    someFailed: (count: number) => `${count} not published`,
    start: "Start the plan",
    continue: "Continue preparing",
    change: "Change",
    detail: (topic: string, readings: number | null) =>
      [topic, readings === null ? null : `${readings} ${readings === 1 ? "reading" : "readings"}`].filter(Boolean).join(" · "),
    commit: "Publish",
    cap: "When you publish",
    publishRecord: (title: string) => `Publish ${title}`,
    publishPlan: "Publish the session plan to learners",
    chooseSome: "Choose what to publish.",
    blockedTitle: (title: string, reason: string) => `${title}: ${reason}`,
    note: "The running order and teaching notes stay private to the teaching team. Coursework publishes from its own author.",
    publishedLine: (titles: string) => `Published: ${titles}`,
    waitsForPlan: "Published readings reach learners once the session plan is published.",
    failedLine: (title: string, reason: string) => `${title} not published.${reason ? ` ${reason}` : ""}`,
    retry: (count: number) => `Retry ${count} not published`,
    retryNote: "Sends only what wasn't published. Nothing else is sent again.",
    unknownCap: "Publication sent, not confirmed",
    unknownSentence: (titles: string) => `The connection dropped before IMAM answered for ${titles}. They may already be published.`,
    check: "Check the publication",
    checkNote: "Publishing again could repeat it. Check first.",
    noRecords: "Save a session plan before adding readings to this session.",
    termClosed: "This term is closed, so work can no longer be set in it.",
    driveNote: "Google Drive or the link's host decides who can open each file.",
    noReadings: "No readings yet.",
    readingsFailed: "Readings couldn't be read.",
    addReading: "+ Reading",
    retryReadings: "Try again",
  },
  runningOrder: {
    label: "Running order",
    job: "What happens in class, in order, for the teaching team.",
    open: "Open running order",
    detail: (count: number) => count ? `${count} ${count === 1 ? "activity" : "activities"} · Teaching team only` : "No activities yet · Teaching team only",
  },
  coursework: {
    label: "Coursework",
    job: "The coursework placed in this session: its deadline and next step, authored and reviewed in the canonical workspace.",
    add: "Add coursework",
    noPlan: "Save a session plan first. Coursework placed in this session appears here.",
    none: "No coursework is placed in this session.",
    unread: "This session's coursework couldn't be read.",
    count: (count: number) => `${count} ${count === 1 ? "work item" : "work items"}`,
    notPublished: (count: number) => `${count} not published`,
    toReview: (count: number) => `${count} to review`,
    draft: "Not published",
    privateDraft: "Private draft",
    privateFacts: "Saved privately · not coursework until published",
    untitled: "Untitled coursework",
    closed: "Closed to new hand-ins",
    noDue: "No due date",
    due: (when: string) => `Due ${when}`,
    nextDue: (when: string) => `Next due ${when}`,
    open: "Open",
    continue: "Continue writing",
    review: "Review",
    termClosed: "This term is closed. Existing work and feedback remain readable.",
  },
  notes: {
    label: "Teaching notes",
    job: "The teaching team's private notes for this session.",
    open: "Open teaching notes",
    empty: "No notes · Teaching team only",
    some: "Written · Teaching team only",
    unsaved: "Not saved yet · Teaching team only",
  },
  folder: {
    label: "Session folder",
    job: "The optional Google Drive folder for this session's teaching files.",
    open: "Open session folder",
    ready: "In Google Drive",
    pending: "Being created",
    absent: "Not created",
    unread: "This session's folder couldn't be read.",
    unknown: "Outcome unknown · An administrator reconciles it",
    needsCourse: "Needs the course folder first",
  },
  learner: {
    getReady: "Get ready",
    getReadyJob: "Before class: what to do and read, and the way into the meeting.",
    nothingPosted: "Nothing is posted for this session yet.",
    postedLater: "Your instructor's plan appears here once it is published.",
    doesNotMeet: "Does not meet",
    wasSet: (when: string) => `It was set for ${when}.`,
    before: (text: string) => `Before the session: ${text}`,
    readings: (count: number) => (count ? `${count} ${count === 1 ? "reading" : "readings"}` : "No readings"),
    show: "Show",
    after: "After class",
    afterJob: "After class: the recap, next steps and your attendance.",
    notHeld: "Not held yet",
    noRecap: "No recap yet.",
    appearsWhen: (when: string) => `The recap and next steps appear here once ${when}.`,
    instructorCloses: "your instructor closes the session",
    isHeld: (name: string) => `${name} is held`,
    held: (name: string) => `${name} held`,
    nextSteps: (text: string) => `Next steps: ${text}`,
    noNextSteps: "No next steps posted.",
    recordings: "Recordings",
  },
  followUp: {
    label: "Follow up",
    show: "Show",
    job: "What the last session left: its close, its review and the work still open, each in its own row.",
    unread: "What the last session left couldn't be read.",
    nothing: "Nothing waiting",
    notAllRead: "Not all read",
    held: (when: string) => `Held ${when}`,
    ended: (when: string) => `Ended ${when}`,
    close: "Close",
    review: "Review",
    afterClass: "After class",
    yourWork: "Your work",
    courseWide: "Course-wide work",
    earlier: "Earlier sessions",
    notClosed: (name: string) => `${name} not closed`,
    recapWaits: "Recap not published",
    awaiting: (count: number) => `${count} ${count === 1 ? "attempt" : "attempts"} awaiting review`,
    returned: "Returned for revision",
    rollRecorded: (unrecorded: number | null) => `Roll recorded${unrecorded ? ` · ${unrecorded} unrecorded` : ""}`,
    rollNotRecorded: "Roll not recorded",
    rollUnread: "The roll couldn't be read",
    recap: (state: string) => `Recap and next steps ${state}`,
    recapUnread: "Recap and next steps couldn't be read",
    reviewUnread: (name: string) => `Attempts for ${name} couldn't be read.`,
    noReview: "No IMAM attempts awaiting review",
    youWere: (status: string) => `You were ${status}`,
    attendanceNotRecorded: "Your attendance not recorded",
    attendanceUnread: "Your attendance couldn't be read",
    recapPublished: "Recap and next steps published",
    noRecap: "No recap yet",
    learnerRecapUnread: "The recap couldn't be read",
    workWords: (state: "revision_open" | "to_hand_in" | "waiting_feedback", count: number) =>
      state === "revision_open" ? `${count} returned for revision` : state === "to_hand_in" ? `${count} to hand in` : `${count} handed in, waiting for feedback`,
    workUnread: (name: string) => `Your work for ${name} couldn't be read.`,
    wideTeaching: (count: number) => `${count} awaiting review across the course`,
    wideLearning: (count: number) => `${count} to work on across the course`,
    wideUnread: "Course-wide work couldn't be read.",
    earlierUnread: "Earlier sessions that aren't closed couldn't be read.",
    earlierMany: (count: number) => `${count} earlier sessions not closed`,
    endedOn: (day: string) => `Ended ${day}`,
    cancelledRow: (when: string) => `${when} · Cancelled. No class meets.`,
    reading: (name: string) => `Reading ${name}`,
    readFailed: (name: string) => `${name} couldn't be read.`,
    readRefused: (name: string) => `${name} isn't available to you.`,
    readStale: (name: string, time: string) => `${name} couldn't be refreshed. As read at ${time}.`,
    tryAgain: "Try again",
    openClose: "Close",
    openHeld: "Open",
    reviewAct: "Review",
    readRecap: "Read",
    openWork: "Open",
    coursework: "Coursework",
    allSessions: "All sessions",
    closeSession: (name: string) => `Close ${name}`,
    publishRecap: "Publish recap",
    reviewAttempts: (count: number) => `Review ${count} ${count === 1 ? "attempt" : "attempts"}`,
    revise: "Revise",
  },
  close: {
    label: "Close",
    job: "After class: record the roll, mark the session held, and choose whether the recap and next steps reach learners.",
    roll: "Roll",
    recap: "Recap and next steps",
    recording: "Recording",
    held: "Held",
    notTaken: "Roll not taken",
    /** Before the session meets, Close waits quietly for its turn. */
    notYet: "After class",
    marked: (marked: number, total: number) => `${marked} of ${total} marked`,
    allMarked: "Roll marked, not recorded",
    changed: "Roll changed since you opened it",
    rollChanges: "Roll changes, not recorded",
    notConfirmed: "Close not confirmed",
    recapFailed: "Recap not published",
    rollNotSaved: "Roll changes not saved",
    rollReading: "Reading the roll",
    rollFailed: "The roll couldn't be read. No learners are shown.",
    take: "Take roll",
    continue: "Continue roll",
    update: "Update roll",
    learners: (total: number) => `${total} ${total === 1 ? "learner" : "learners"}`,
    recapDraft: "recap draft",
    recapNotWritten: "recap not written",
    published: "recap published",
    keptPrivate: "recap kept private",
    notRecorded: "Not recorded",
    recorded: "Recorded",
    written: "Written, not published",
    nothingWritten: "Nothing written",
    noRecording: "No recording",
    recordingUnread: "Open to read",
    recordingPublished: "Published",
    recordingNotPublished: "Not published",
    commit: "Close session",
    saveRoll: "Save roll changes",
    publishRecap: "Publish the recap",
    cap: "When you close",
    capUpdate: "When you save the roll",
    capPublish: "When you publish",
    recordRoll: (summary: string) => `Record the roll: ${summary}`,
    markHeld: "Mark held",
    staysHeld: (session: string) => `${session} stays held`,
    publishTo: (fields: string, total: number) => `Publish the ${fields} to ${total} ${total === 1 ? "learner" : "learners"}`,
    recapUnchanged: "Recap and next steps unchanged",
    noteDraft: "Marks stay a draft in this browser until you close.",
    notePrivate: (fields: string) => `Unticked: the ${fields} stay private to the teaching team.`,
    noteNothing: "Nothing written to publish. The published plan stays as it is.",
    noneMarked: "Mark at least one learner.",
    leftUnrecorded: (n: number) => `Leave ${n} not marked: ${n === 1 ? "that learner stays" : "those learners stay"} unrecorded`,
    leftUnrecordedLine: (n: number) => `${n} not marked, left unrecorded`,
    chooseRoll: "Choose which roll to keep.",
    planWaits: "Publishing waits until your latest plan edits are saved.",
    planObjective: "Add a session objective before publishing.",
    planConflict: "Resolve the newer plan version first.",
    planMissing: "Publishing needs a saved plan.",
    closed: (session: string) => `Closed ${session}`,
    checked: (session: string) => `Checked: ${session} is closed`,
    rollUpdated: (session: string) => `Roll updated for ${session}`,
    heldLine: (session: string) => `${session} held`,
    rollLine: (summary: string) => `Roll recorded: ${summary}`,
    publishedLine: (fields: string, total: number) => `${fields} published to ${total} ${total === 1 ? "learner" : "learners"}`,
    privateLine: (fields: string) => `${fields} kept private to the teaching team`,
    nothingPublished: "Nothing new published",
    recordingRemains: "Still open: add a recording link once it exists",
    notClosed: "Not closed.",
    rollNotUpdated: "Roll not updated.",
    refusedFallback: "IMAM refused the roll. Your marks are kept in this browser.",
    unknownCap: "Close sent, not confirmed",
    unknownSentence: (session: string) => `The connection dropped before IMAM answered, so ${session} may already be held.`,
    check: "Check Close result",
    checkNote: "Closing again could repeat it. Check first.",
    partialFailed: (fields: string) => `${fields} not published. Saved, still private.`,
    retryNote: "Sends only the recap and next steps. Held and the roll stay recorded.",
    publishFailed: "The session plan wasn't published. Your changes are kept.",
    publishUnknown: "The publication may have landed. Check before publishing again.",
    publishUnknownCap: "Publication sent, not confirmed",
    checkPublication: "Check the publication",
    checkPublicationNote: "Reads the plan's own send. It never publishes again.",
    planUnread: "The session plan couldn't be read, so nothing was published. Your changes are kept. Try again.",
    publishNotCommitted: "Nothing was published by that send. Your changes are kept, so you can publish them again.",
    closeNotCommitted: "Nothing was recorded by that send. Your roll and recap are kept, so you can close again.",
    checkPublish: "Check the publication",
    rollConflict: (when: string) => `A newer roll was recorded${when}. Your marks are kept here.`,
    nextMeets: (name: string, day: string) => `Next session that meets: ${name}, ${day}`,
  },
} as const;

/** All sessions (C07): the register, a session's Move, Cancel or Reinstate in its row, and Cancel N sessions. */
export const ALL_SESSIONS_COPY = {
  label: "All sessions",
  job: "Every session of the course, in date order, with its plan and roll.",
  count: (n: number) => `${n} ${n === 1 ? "session" : "sessions"}`,
  none: (teaching: boolean) => (teaching ? "No dated sessions in this course." : "No sessions are scheduled yet."),
  noneSub: "Sessions appear here once they are scheduled.",
  find: "Session name or date",
  /** Calendar order's one filter beside the find (#1393 M2). */
  which: "Sessions",
  allSessions: "All sessions",
  cancelledOnly: "Cancelled only",
  current: "Current",
  next: "Next",
  heldRun: (from: string, to: string) => `Sessions ${from} to ${to}`,
  itsPlan: "its plan",
  select: "Select",
  show: "Show",
  /** C10: a learner's published plans on no session, the List's last rows under this group line. */
  unplaced: "Not on a session yet",
  plans: (n: number) => `${n} ${n === 1 ? "plan" : "plans"}`,
  objectives: "Session objectives",
  preparation: "Before the session",
  held: "Held",
  closedTerm: "This term is closed. The sessions are a read-only record.",
  readFailed: "All sessions could not be read.",
  forbidden: "Sessions are not available to you.",
  forbiddenBack: "Back to the course",
  stale: "Couldn't refresh. A later change isn't shown.",
  prepare: "Prepare",
  close: "Close",
  /** A cancelled session's plan, put on a session that meets (its plan editor's own field). */
  movePlan: "Move plan",
  open: "Open session",
  pick: (name: string) => `Select ${name}`,
  reading: (name: string) => `Reading ${name}...`,
  readRowFailed: (name: string) => `${name} could not be read.`,
  readRowRefused: (name: string) => `${name} isn't available to you.`,
  readRowStale: (name: string, time: string) => `${name} couldn't be refreshed. As read at ${time}.`,
  tryAgain: "Try again",
  change: {
    verb: { move: "Move session", cancel: "Cancel session", reinstate: "Reinstate session" },
    cap: { move: "When you move it", cancel: "When you cancel it", reinstate: "When you reinstate it" },
    when: "New date and time",
    date: "New date",
    time: "New start time",
    keepsLength: (zone: string) => `${zone}. The session keeps its length.`,
    note: "Note for students",
    noteHelp: "Up to 200 characters. Students read it on the session page.",
    noteMissing: "Write a note for students.",
    /** After a confirmed change the editor is empty again; the note it saved is still said (S5 READ finding 4). */
    noteSaved: "Saved",
    savedNote: (note: string) => `Saved with the change: ${note}`,
    dateMissing: "Choose the new date.",
    timeMissing: "Choose the new start time.",
    moves: (name: string, when: string) => `Move ${name} to ${when}`,
    cancels: (name: string, when: string) => `Cancel ${name}, ${when}. Its plan and records are kept`,
    reinstates: (name: string, when: string) => `Put ${name} back on the schedule, ${when}`,
    keeps: "Its number, plan, readings, tasks and meeting link stay with it",
    reinstatesToo: "Moving it also puts it back on the schedule",
    readsNote: "Students read your note on the session page",
    email: "Email the note to enrolled students",
    /** CC2: a draft course's change tells no one, now or at publication. */
    readsNoteLater: "Students read your note on the session page once the course is published",
    noEmail: "No email is sent: learners are not told about changes before the course is published",
    done: { move: (name: string) => `${name} moved`, cancel: (name: string) => `${name} cancelled`, reinstate: (name: string) => `${name} reinstated` },
    unknownCap: "Not confirmed",
    unknown: (name: string) => `The change to ${name} may have saved. Check before changing it again.`,
    check: "Check",
    checkNote: "Reads whether the change was saved. It never sends it again.",
    notCommitted: "Nothing was changed by that send. Your note is kept, so you can send it again.",
    changedSince: (name: string) => `${name} changed after you began. Compare before you send.`,
    nowWhen: "Now",
    keepMine: "Keep my change",
    loadNewer: "Use the session as it is now",
  },
  bulk: {
    label: "Cancel sessions",
    job: "Cancel several future sessions together, such as break dates.",
    resting: "Select sessions to cancel together",
    restingDetail: "Choose future sessions in All sessions, such as break dates.",
    review: (n: number) => `Review ${n} selected`,
    ready: "Ready to cancel",
    verb: (n: number) => `Cancel ${n} ${n === 1 ? "session" : "sessions"}`,
    cap: "When you cancel them",
    cancelled: (n: number) => `${n} ${n === 1 ? "session" : "sessions"} cancelled`,
    partly: "Partly cancelled",
    notCancelled: "Not cancelled",
    retry: (n: number) => `Cancel the ${n} that failed`,
    retryNote: "Sends only the sessions that failed, under the same request.",
    unknown: "The cancellation may have saved. Check before cancelling again.",
  },
} as const;

/** C08 Not on a session yet: the teaching team's plans made before their sessions exist. */
export const UNPLACED_COPY = {
  label: "Not on a session yet",
  job: "The plans made before their sessions exist, each edited and put on a session in its row.",
  on: (session: string) => `On ${session}`,
  count: (n: number) => `${n} ${n === 1 ? "plan" : "plans"}`,
  none: "A plan made before its session exists waits here until it is put on one.",
  newPlan: "New plan",
  notSaved: "Not saved yet",
  edit: "Edit",
  read: "Read",
} as const;

/** Putting a plan on a session: C08's row Task, and C07's Move plan for a cancelled session's plan. */
export const PLACE_COPY = {
  session: "Session",
  choose: "Choose a session",
  noneChosen: "No session chosen",
  unscheduled: "Unscheduled session",
  help: "Only sessions that meet and have no plan are listed. Moving a published plan moves what students see with it.",
  verb: "Put on a session",
  cap: "When you put it on a session",
  verbMove: "Move plan",
  capMove: "When you move it",
  chosenSession: "the session you choose",
  places: (topic: string, session: string) => `Put ${topic} on ${session}`,
  leaves: (session: string) => `It leaves ${session}, which was cancelled`,
  published: "Students see its published plan on that session",
  unpublished: "Students see nothing until it is published",
  chooseMissing: "Choose a session.",
  gone: "That session no longer meets. Choose another.",
  taken: "That session has a plan now. Choose another.",
  missing: "This plan is no longer in the course.",
  done: (session: string) => `Now on ${session}`,
  notPlaced: "Not put on a session",
  notMoved: "Not moved",
  unknownCap: "Not confirmed",
  unknown: "The plan may have been put on the session. Check before choosing again.",
  check: "Check",
  checkNote: "Reads whether the plan was placed. It never sends it again.",
  notCommitted: "The plan was not moved by that send. Your choice is kept, so you can send it again.",
  changedSince: "This plan changed after you chose its session. Compare before you send.",
  planNow: "The plan now",
  keepChoice: "Keep my choice",
  startAgain: "Choose again",
  unread: "The plan couldn't be read, so nothing was sent. Try again.",
} as const;

/** `session:<id>/review`: another session's Review, visited from Follow up. */
export const REVIEW_VISIT_COPY = {
  noun: "Session",
  label: "Review",
  job: "This session's work, each piece reviewed on its own.",
  review: "Review",
  toReview: (n: number) => `${n} to review`,
  nothing: "Nothing to review",
  none: "No work on this session to review.",
  cancelled: "This session was cancelled, so nothing on it is reviewed.",
  unread: "This session's work couldn't be read.",
} as const;

/** "recap and next steps", "recap" or "next steps": the plan fields a commit would publish. */
export function fieldNames(names: string[]): string {
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : names[0] ?? "";
}

/** "Session 5", or the session's own label. */
export function sessionName(session: { session_no: number | null; label: string | null }): string {
  return session.session_no == null ? session.label?.trim() || "Session" : `Session ${session.session_no}`;
}
